import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { AuthUser, RegisterData } from '@/types'

const AUTH_ENDPOINT = import.meta.env.PROD
  ? '/api'
  : (import.meta.env.VITE_AUTH_ENDPOINT || '')

interface UpdateProfileData {
  firstName: string
  lastName: string
  city?: string
  country?: string
}

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  login: (identifier: string, password: string, turnstileToken?: string) => Promise<void>
  loginWithGoogle: (credential: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  activateAccount: (token: string, password: string, confirmPassword: string, turnstileToken?: string) => Promise<void>
  forgotPassword: (email: string, turnstileToken: string) => Promise<void>
  resetPassword: (token: string, password: string, confirmPassword: string) => Promise<void>
  updateProfile: (data: UpdateProfileData) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      // TODO: Check authentication status with backend
      // For now, simulate loading
      await new Promise(resolve => setTimeout(resolve, 1000))
      setLoading(false)
    } catch (error) {
      console.error('Auth check failed:', error)
      setLoading(false)
    }
  }

  const login = async (identifier: string, password: string, turnstileToken?: string) => {
    try {
      // Use the actual Lambda Function URL
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({ identifier, password, turnstileToken, action: 'login' }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Login failed')
      }

      const data = await response.json()
      setUser(data.user)
    } catch (error) {
      throw error
    }
  }

  const loginWithGoogle = async (credential: string) => {
    const response = await fetch(AUTH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'include',
      body: JSON.stringify({ credential, action: 'googleLogin' }),
    })
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Google login failed')
    }
    const data = await response.json()
    setUser(data.user)
  }

  const register = async (data: RegisterData) => {
    try {
      // Detect browser language
      const browserLanguage = navigator.language.split('-')[0] || 'en'
      
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({ ...data, language: browserLanguage, action: 'register' }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Registration failed')
      }

      // Registration successful, user needs to activate account
    } catch (error) {
      throw error
    }
  }

  const activateAccount = async (token: string, password: string, confirmPassword: string, turnstileToken?: string) => {
    try {
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({ token, password, confirmPassword, turnstileToken, action: 'setPassword' }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Account activation failed')
      }
    } catch (error) {
      throw error
    }
  }

  const forgotPassword = async (email: string, turnstileToken: string) => {
    const browserLanguage = navigator.language.split('-')[0] || 'en'
    const response = await fetch(AUTH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ email, language: browserLanguage, turnstileToken, action: 'forgotPassword' }),
    })
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Request failed')
    }
  }

  const resetPassword = async (token: string, password: string, confirmPassword: string) => {
    const response = await fetch(AUTH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ token, password, confirmPassword, action: 'resetPassword' }),
    })
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Password reset failed')
    }
  }

  const updateProfile = async (data: UpdateProfileData) => {    try {
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({ ...data, action: 'updateProfile' }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Profile update failed')
      }

      const responseData = await response.json()
      setUser(responseData.user)
    } catch (error) {
      throw error
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword, action: 'changePassword' }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Password change failed')
      }
    } catch (error) {
      throw error
    }
  }

  const logout = async () => {
    try {
      await fetch(AUTH_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'include',
        body: JSON.stringify({ action: 'logout' }),
      })
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setUser(null)
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    loginWithGoogle,
    register,
    activateAccount,
    forgotPassword,
    resetPassword,
    updateProfile,
    changePassword,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}